import { z } from 'zod';

import {
    MARKET_CATALOGUE_CODE_MAX_LENGTH,
    PREFLIGHT_MARKET_KINDS,
    type PreflightMarketKind,
} from './market-catalogue.js';

/**
 * Placeholder for a report with no market (they predate the backfill, KTD6).
 * Lives here so the frontend export libs and the API's PDF renderer emit the
 * same words; the UI renders the translated `preflight.markets.none` instead.
 */
export const MARKET_NONE_LABEL = 'No market';

/**
 * How a market is named in an export: name and code together, because the code alone
 * is ambiguous ("NA" is Namibia and North America) and the name alone drops the
 * identifier a reader pivots on. A report with no market gets the placeholder rather
 * than a blank, so tabular formats keep their cell count.
 */
export function marketExportLabel(market: { name: string; code: string } | null): string {
    return market ? `${market.name} (${market.code})` : MARKET_NONE_LABEL;
}

/**
 * A catalogue entry's identity: kind and code together, never code alone, because
 * continent codes collide with ISO alpha-2 — `NA` is both Namibia and North America.
 * Every duplicate check compares this key, so they cannot drift apart (KTD3).
 */
export function marketEntryKey(ref: { kind: PreflightMarketKind; code: string }): string {
    return `${ref.kind}:${ref.code}`;
}

export const PreflightMarketKindSchema = z.enum(PREFLIGHT_MARKET_KINDS);

/**
 * Transport shape for a market identity. Existence and active status are
 * validated against the DB catalogue at the repository boundary — this schema
 * must not consult the compiled constant.
 */
export const AddPreflightMarketSchema = z.object({
    kind: PreflightMarketKindSchema,
    code: z.string().trim().toUpperCase().min(1).max(MARKET_CATALOGUE_CODE_MAX_LENGTH),
});
export type AddPreflightMarketInput = z.infer<typeof AddPreflightMarketSchema>;

/**
 * `kind` travels with every market because neither half identifies a catalogue
 * entry alone: `NA` is Namibia and North America, so a reader given only the code
 * cannot pick the right icon.
 */
export const PreflightMarketSchema = z.object({
    id: z.string(),
    kind: PreflightMarketKindSchema,
    name: z.string(),
    code: z.string(),
});
export type PreflightMarket = z.infer<typeof PreflightMarketSchema>;

export const PreflightMarketWithCountSchema = PreflightMarketSchema.extend({
    reportCount: z.number().int().nonnegative(),
});
export type PreflightMarketWithCount = z.infer<typeof PreflightMarketWithCountSchema>;
